import {useCallback, useEffect, useRef, useState} from 'react';
import type {RefObject} from 'react';
import {useLatest} from 'ahooks';
import {WIZARD_HEADER_SELECTOR} from '@Libraries/social-media-wizard-library';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import type {SocialPost} from '@Components/social-media/post.vo';
import {getDeepLinkedDesignSelectorTab} from '@Libraries/social-media-grid.library';
import {
  getInitialDesignSelectorTab,
  getStartWizardStep,
  getStepViewedSection,
  getStepViewedType,
  isInvalidStepCombination,
} from '@Libraries/social-media-wizard-step-library';
import {
  AddAccountEvent,
  SocialMediaWizardStep,
  WIZARD_CONTENT_SCROLL_EDGE_THRESHOLD_PX,
  type UseWizardScrollStateResult,
  type WizardStepOrder,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  clearPolledPreviewUrls,
  disableNextButton,
  enableNextButton,
  hideAccountLevelErrorMessage,
  initPublishingParams,
  removeAccountFromPublishingParams,
  removeAccountIdFromPublishing,
  resetAccountResizeStatusForDesignsMap,
  startWizardAt,
  syncEditedDesign,
  syncPublishingParams,
  updateAccountIdsToPublishTo,
  updateAccountLevelErrorMessage,
  updateAccountNeedsResizeMap,
  updateAccountTypeToConnect,
  updateDesignSelectorTab,
  updateDesignsGraphicMetaMap,
  updateDesignsGraphicURLs,
  updateDesignsToPublish,
  updateEmphasizedPlatform,
  updateHasSeenConfetti,
  updateHasSeenOnboarding,
  updateHasUserPublishedBefore,
  updateIsGoogleBusinessProfileEnabled,
  updateIsSocialMediaWizardAiCaptionsEnabled,
  updateIsTemplateSelected,
  updateNumParentAccountsConnected,
  updatePost,
  updatePublishingAction,
  updatePublishingParams,
  updateTriggerNext,
  updateTriggerPrevious,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {doHideRemoveAccountConfirmationForUser, getHideSMRemoveAccountConfirmationPreference} from '@Libraries/social-media-wizard-accounts-library';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';
import {useAppDispatch} from '@/hooks';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {addClickOutsideListener} from '@Utils/dom.util';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {isUserPremium} from '@Libraries/user.library';
import {requestResetAiCaptionInsights} from '@Libraries/auto-caption-library';
import type {DesignMetaUpdateInfo} from '@Components/social-media-wizard/social-media-wizard.types';
import type {DesignSelection, DesignsMetaForPublishing, GeneratedPreviewsStatus, SocialPlatform} from '@Components/social-media/social-media.types';
import {getCaptionErrorAccountId, getTitleErrorAccountId} from '@Libraries/social-media-params.library';
import {areAllDesignPagesSupportedForAccounts, doesVideoExist} from '@Libraries/social-media-resize.library';
import {buildGraphicsMetaMap, getGraphicMeta, getSanitizedEmphasizedPlatform, isSingleDesignSelected} from '@Libraries/social-media-wizard-design-library';
import type {GraphicMeta, GraphicsMetaMapType, PageGraphicsMetaMap, PosterHashedId} from '@Components/social-media/publish-params.types';
import {MultiPostDesignActionPurposes, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {openErrorModal} from '@Modals/error-modal';
import {asyncnoop} from '@Utils/general.util';
import {CONFIRM_ICON_EDITOR_OPTS, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import {consumeWizardOriginCookie} from '@Libraries/social-media-wizard-library';
import {getPostersPageIds, pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import type {SocialPostsDesignActionModalProps} from '@Components/modals/social-post-design-action-modal';
import {openDesignActionModal} from '@Components/modals/social-post-design-action-modal';
import {savePreferredProfileSelectionUser} from '@Components/social-media/social-media-thunk';
import {useAccountIdsToPublishTo, useSocialPostPublishingAction, useWizardStep} from '@Components/social-media/social-media.hooks';
import {useWizardCaptionErrors, useWizardConnectedAccounts, useWizardTitleErrors} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {SocialAccount} from '@Components/social-media/account.vo';

interface PopStateEventWithStep extends PopStateEvent {
  state: {
    step: SocialMediaWizardStep;
  };
}

interface UseWizardNavigationHistoryOpts {
  wizardStep: SocialMediaWizardStep;
  post: SocialPost | null;
}

interface UseWizardNavigationHistoryResult {
  pushStep: VoidFunction;
  updateWindowHistoryState: (step: SocialMediaWizardStep) => void;
  popPreviousStep: () => SocialMediaWizardStep | undefined;
}

interface UseWizardWindowEventsOpts {
  onBack: VoidFunction;
}

interface UseWizardWindowEventsResult {
  confirmLeavingWithUnsavedChanges: () => boolean;
}

interface UseAiCaptionWizardSetupOpts {
  connectedAccountIds: number[];
  postId: string | null;
}

interface UseEmbeddedEditorFlowOpts {
  designsToPublishTo: DesignSelection[];
  designsGraphicsMetaMap: GraphicsMetaMapType;
  design: DesignSelection | undefined;
  updatePublishingDesignsMeta: (freshDesign?: DesignSelection) => Promise<DesignMetaUpdateInfo>;
  updateDesignsMetaOnClosingEmbeddedEditor: () => Promise<void>;
  setIsPreviewLoading: (loading: boolean) => void;
  setIsLoadingEmbeddedEditor: (loading: boolean) => void;
}

interface UseEmbeddedEditorFlowResult {
  onClickEditDesign: VoidFunction;
  onEmbeddedEditorOpened: VoidFunction;
  onEmbeddedEditorForTemplateClosed: (designIdEdited: PosterHashedId) => Promise<void>;
  triggerEditDesignsFlow: VoidFunction;
}

interface UseTrackWizardStepViewedOpts {
  wizardStep: SocialMediaWizardStep;
  hasStartedLoggedOut: boolean;
  hasResolvedStartStep: boolean;
}

interface UseWizardInitOpts {
  post: SocialPost | undefined;
  accounts: SocialAccount[];
  accountIdsForPost: number[];
  design: DesignSelection | undefined;
  template: DesignSelection | undefined;
  isGalleryFlow: boolean;
  isSelectTemplateFlow: boolean;
  emphasizedPlatform: SocialPlatform | undefined;
  hasSeenOnboarding: boolean;
  hasSeenConfetti: boolean;
  hasUserPublishedBefore: boolean;
  isSocialMediaWizardAiCaptionsEnabled: boolean;
  isGoogleBusinessProfileEnabled: boolean;
  numParentAccountsConnected: number;
  stepOrder: WizardStepOrder;
  setDesignsToPublish: (designs: DesignSelection[]) => void;
}

export function useWizardNavigationHistory({wizardStep, post}: UseWizardNavigationHistoryOpts): UseWizardNavigationHistoryResult {
  const dispatch = useAppDispatch();
  const [previousSteps, setPreviousSteps] = useState<SocialMediaWizardStep[]>([]);
  const prevStepRef = useRef<SocialMediaWizardStep | null>(wizardStep);
  const currentStepRef = useLatest(wizardStep);
  const skipNextHistoryPushRef = useRef(false);

  const pushStep = (): void => {
    if (wizardStep && prevStepRef.current && !isInvalidStepCombination(prevStepRef.current, wizardStep, post)) {
      setPreviousSteps([...previousSteps, prevStepRef.current]);
    }

    prevStepRef.current = wizardStep;
  };

  const popPreviousStep = (): SocialMediaWizardStep | undefined => {
    const previousStep = previousSteps[previousSteps.length - 1];

    if (previousStep !== undefined) {
      setPreviousSteps(previousSteps.slice(0, -1));
      prevStepRef.current = null;
    }

    return previousStep;
  };

  const updateWindowHistoryState = (step: SocialMediaWizardStep): void => {
    if (skipNextHistoryPushRef.current) {
      skipNextHistoryPushRef.current = false;
      return;
    }

    if (window.history.state === null) {
      window.history.replaceState({step}, '', '');
      return;
    }

    window.history.pushState({step}, '', '');
  };

  useEffect((): VoidFunction => {
    const onPopStateChange = (popStateEvent: PopStateEventWithStep): void => {
      if (typeof popStateEvent.state === 'object') {
        const {step} = popStateEvent.state;

        if (step && step !== currentStepRef.current) {
          skipNextHistoryPushRef.current = true;
          dispatch(updateWizardStep(step));
        }
      }
    };

    window.addEventListener('popstate', onPopStateChange);

    return (): void => {
      window.removeEventListener('popstate', onPopStateChange);
    };
  }, [dispatch]);

  return {
    pushStep,
    updateWindowHistoryState,
    popPreviousStep,
  };
}

export function useWizardWindowEvents({onBack}: UseWizardWindowEventsOpts): UseWizardWindowEventsResult {
  const wizardStep = useWizardStep();
  const showBrowserConfirmOnExit = useRef<boolean>(false);
  const handlersRef = useLatest({onBack});

  useEffect(() => {
    const confirmNavigation = (event: BeforeUnloadEvent): void => {
      if (showBrowserConfirmOnExit.current) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const handleAddAccount = (): void => {
      handlersRef.current.onBack();
    };
    window.addEventListener(AddAccountEvent.ADD_ACCOUNT, handleAddAccount);
    window.addEventListener('beforeunload', confirmNavigation);

    return (): void => {
      window.removeEventListener(AddAccountEvent.ADD_ACCOUNT, handleAddAccount);
      window.removeEventListener('beforeunload', confirmNavigation);
    };
  }, []);

  useDidUpdateEffect(() => {
    showBrowserConfirmOnExit.current = wizardStep === SocialMediaWizardStep.ADD_DETAILS;
  }, [wizardStep]);

  const confirmLeavingWithUnsavedChanges = (): boolean => {
    if (!showBrowserConfirmOnExit.current) {
      return true;
    }

    if (!window.confirm(window.i18next.t('pmwjs_you_have_unsaved_changes'))) {
      return false;
    }

    showBrowserConfirmOnExit.current = false;

    return true;
  };

  return {confirmLeavingWithUnsavedChanges};
}

export function useAiCaptionWizardSetup({connectedAccountIds, postId}: UseAiCaptionWizardSetupOpts): void {
  const {setInAutoCaptionMode} = useAutoCaptionGlobal();
  const {fetchHasSeenStatus} = useHasSeenAiCaptionIntroPopup();
  const isAiCaptionInsightsResetDone = useRef<boolean>(false);

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    void fetchHasSeenStatus();
  }, []);

  useEffect(() => {
    const removeListener = addClickOutsideListener(`.social-caption-container.-in-ai-mode`, onOutsideAiCaptionPromptBoxClick);

    return (): void => {
      removeListener();
    };
  }, []);

  useEffect(() => {
    if (!isUserPremium() || !connectedAccountIds.length || isAiCaptionInsightsResetDone.current || !postId) {
      return;
    }

    void requestResetAiCaptionInsights(postId, connectedAccountIds);
    isAiCaptionInsightsResetDone.current = true;
  }, [connectedAccountIds, postId]);

  const onOutsideAiCaptionPromptBoxClick = (): void => {
    setInAutoCaptionMode({accountId: null});
  };
}

const POLLING_REQUEST_TIMEOUT = 600000;
export const PREVIEW_GENERATION_POLLING_EVENT_ID = 'social-media-wizard-preview-generation';

const isGeneratedPreviewsStatus = (value: unknown): value is GeneratedPreviewsStatus => {
  return typeof value === 'object' && value !== null && 'allGenerated' in value;
};

interface UseDesignMetaSyncOpts {
  designsToPublishTo: DesignSelection[];
  postId: string | undefined;
  accountIdsToPublishTo: number[];
}

interface UseDesignMetaSyncResult {
  updatePublishingDesignsMeta: (freshDesign?: DesignSelection) => Promise<DesignMetaUpdateInfo>;
  updateDesignsMetaOnClosingEmbeddedEditor: () => Promise<void>;
  fetchGraphicsMetaForAnonymousTemplates: (designToFetch: DesignSelection) => Promise<void>;
  checkUpdateInGeneratedPreviewsStatus: (designs: DesignSelection[], isCancelled: () => boolean) => Promise<boolean>;
}

export function useDesignMetaSync({designsToPublishTo, postId, accountIdsToPublishTo}: UseDesignMetaSyncOpts): UseDesignMetaSyncResult {
  const dispatch = useAppDispatch();

  const updatePublishingDesignsMeta = async (freshDesign?: DesignSelection): Promise<DesignMetaUpdateInfo> => {
    if (designsToPublishTo.length === 0) {
      return {hasAnyVideo: false, areAllAccountsSupported: true};
    }

    const designsToUpdate = freshDesign ? [freshDesign] : designsToPublishTo;

    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToUpdate),
      postId: postId ?? null,
    })) as DesignsMetaForPublishing;

    const designMetaUpdateInfo: DesignMetaUpdateInfo = {
      hasAnyVideo: doesVideoExist(data.designsIsVideo),
      areAllAccountsSupported: true,
    };

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      const resizeMap = data.accountNeedsResizeMap;
      designMetaUpdateInfo.areAllAccountsSupported = areAllDesignPagesSupportedForAccounts(resizeMap, accountIdsToPublishTo);
      dispatch(updateAccountNeedsResizeMap(resizeMap));
    }

    dispatch(updateDesignsGraphicMetaMap(buildGraphicsMetaMap(data.previewUrls, data.designsIsVideo, data.designNames, designsToUpdate)));

    return designMetaUpdateInfo;
  };

  const updateDesignsMetaOnClosingEmbeddedEditor = async (): Promise<void> => {
    const data = (await window.PMW.writeLocal('socialmedia/updateDesignSelectionMetaForPublishing', {
      designSelections: JSON.stringify(designsToPublishTo),
      postId: postId ?? null,
    })) as DesignsMetaForPublishing;

    dispatch(updatePost(data.post));

    if (data.accountNeedsResizeMap) {
      dispatch(updateAccountNeedsResizeMap(data.accountNeedsResizeMap));
    }

    dispatch(updateDesignsGraphicMetaMap(buildGraphicsMetaMap(data.previewUrls, data.designsIsVideo, data.designNames, designsToPublishTo)));
  };

  const fetchGraphicsMetaForAnonymousTemplates = async (designToFetch: DesignSelection): Promise<void> => {
    const graphicMeta = await getGraphicMeta(designToFetch);

    const graphicsMetaMap: GraphicsMetaMapType = {};
    graphicsMetaMap[designToFetch.posterHashedId] ??= {};
    graphicsMetaMap[designToFetch.posterHashedId][designToFetch.pageHashedId] = graphicMeta;

    dispatch(updateDesignsGraphicMetaMap(graphicsMetaMap));
  };

  const checkUpdateInGeneratedPreviewsStatus = async (designs: DesignSelection[], isCancelled: () => boolean): Promise<boolean> => {
    const pollingHaveAllUpdatedPreviewsBeenGenerated = async (): Promise<void> => {
      return (await window.PMW.writeLocal('poster/haveAllUpdatedPreviewsBeenGenerated', {designs: JSON.stringify(designs)})) as Promise<void>;
    };

    try {
      const generatedPreviews: unknown = await window.PMW.pollingAjaxCallAsync(
        asyncnoop,
        pollingHaveAllUpdatedPreviewsBeenGenerated,
        POLLING_REQUEST_TIMEOUT,
        PREVIEW_GENERATION_POLLING_EVENT_ID
      );

      if (isCancelled()) {
        return false;
      }

      if (generatedPreviews === true) {
        return true;
      }

      if (isGeneratedPreviewsStatus(generatedPreviews) && generatedPreviews.allGenerated) {
        dispatch(updateDesignsGraphicURLs(generatedPreviews.previewUrls));
        return true;
      }

      openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_poster_previews')});
    } catch {
      if (!isCancelled()) {
        openErrorModal({message: window.i18next.t('pmwsjs_unable_to_fetch_posters')});
      }
    }

    return false;
  };

  return {
    updatePublishingDesignsMeta,
    updateDesignsMetaOnClosingEmbeddedEditor,
    fetchGraphicsMetaForAnonymousTemplates,
    checkUpdateInGeneratedPreviewsStatus,
  };
}

export function useEmbeddedEditorFlow({
  designsToPublishTo,
  designsGraphicsMetaMap,
  design,
  updatePublishingDesignsMeta,
  updateDesignsMetaOnClosingEmbeddedEditor,
  setIsPreviewLoading,
  setIsLoadingEmbeddedEditor,
}: UseEmbeddedEditorFlowOpts): UseEmbeddedEditorFlowResult {
  const dispatch = useAppDispatch();

  const resetResizeStatesOnClosingEmbeddedEditor = (): void => {
    dispatch(resetAccountResizeStatusForDesignsMap());
  };

  const onEmbeddedEditorOpened = (): void => {
    setIsLoadingEmbeddedEditor(false);
  };

  const onEmbeddedEditorClosed = async (posterId: PosterHashedId): Promise<void> => {
    dispatch(clearPolledPreviewUrls());
    await updatePublishingDesignsMeta(design);

    const updatedEntries = Object.entries(designsGraphicsMetaMap[posterId]).map(([pageHashId, graphicMeta]) => {
      const newGraphicMeta: GraphicMeta = {
        ...graphicMeta,
        graphicURL: '',
        isVideo: false,
      };

      return [pageHashId, newGraphicMeta];
    });

    const loadingGraphicsPageMetas = Object.fromEntries(updatedEntries) as PageGraphicsMetaMap;

    const newGraphicsMetaMap: GraphicsMetaMapType = {
      ...designsGraphicsMetaMap,
      [posterId]: loadingGraphicsPageMetas,
    };

    dispatch(updateDesignsGraphicMetaMap(newGraphicsMetaMap));

    setIsPreviewLoading(true);

    const onPreviewGenerationSuccess = (): void => {
      resetResizeStatesOnClosingEmbeddedEditor();
      void updateDesignsMetaOnClosingEmbeddedEditor().finally(() => {
        setIsPreviewLoading(false);
      });
    };

    const onPreviewGenerationFailure = (err: unknown): void => {
      console.error(err);
      setIsPreviewLoading(false);
    };

    pollForPosterPreviewGenerationStatus(posterId, onPreviewGenerationSuccess, onPreviewGenerationFailure);
  };

  const onEditDesignSelectionCallback = (designToEdit: DesignSelection): void => {
    setIsLoadingEmbeddedEditor(true);

    openDesignInEmbeddedEditor(
      designToEdit.posterHashedId,
      onEmbeddedEditorOpened,
      () => {
        void onEmbeddedEditorClosed(designToEdit.posterHashedId);
      },
      undefined,
      undefined,
      undefined,
      CONFIRM_ICON_EDITOR_OPTS
    );
  };

  const triggerEditDesignsFlow = (): void => {
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_EDITDESIGN);

    if (isSingleDesignSelected(designsToPublishTo)) {
      onEditDesignSelectionCallback(designsToPublishTo[0]);
      return;
    }

    const designActionModal: SocialPostsDesignActionModalProps = {
      designsToPublish: designsToPublishTo,
      designsGraphicsMetaMap,
      designActionCallback: onEditDesignSelectionCallback,
      actionPurpose: MultiPostDesignActionPurposes.EDIT,
      showRemoveDesignCta: true,
      removeDesignCallback: (designs: DesignSelection[]): void => {
        setIsPreviewLoading(true);
        dispatch(updateDesignsToPublish(designs));
      },
    };

    openDesignActionModal(designActionModal);
  };

  const onClickEditDesign = (): void => {
    triggerEditDesignsFlow();
  };

  const onEmbeddedEditorForTemplateClosed = async (designIdEdited: PosterHashedId): Promise<void> => {
    const pageIdsMap = await getPostersPageIds([designIdEdited]);
    const pageId = pageIdsMap[designIdEdited].at(0);

    if (!pageId) {
      return;
    }

    const designEdited: DesignSelection = {posterHashedId: designIdEdited, pageHashedId: pageIdsMap[designIdEdited][0]};

    if (designEdited.posterHashedId !== designsToPublishTo[0].posterHashedId || designEdited.pageHashedId !== designsToPublishTo[0].pageHashedId) {
      dispatch(syncEditedDesign(designEdited));
    }

    const {areAllAccountsSupported} = await updatePublishingDesignsMeta(designEdited);

    setIsPreviewLoading(true);

    pollForPosterPreviewGenerationStatus(
      designEdited.posterHashedId,
      (): void => {
        dispatch(clearPolledPreviewUrls());
        void updatePublishingDesignsMeta(designEdited).finally(() => {
          setIsPreviewLoading(false);
        });
      },
      (err: unknown): void => {
        console.error(err);
        setIsPreviewLoading(false);
      }
    );

    if (areAllAccountsSupported) {
      dispatch(updateTriggerNext());
      dispatch(hideAccountLevelErrorMessage());
    } else {
      dispatch(
        updateAccountLevelErrorMessage({
          title: window.i18next.t('pmwjs_unsupported_account_error_title'),
          description: window.i18next.t('pmwjs_unsupported_account_error_desc'),
        })
      );
      dispatch(updateTriggerPrevious());
    }
  };

  return {
    onClickEditDesign,
    onEmbeddedEditorOpened,
    onEmbeddedEditorForTemplateClosed,
    triggerEditDesignsFlow,
  };
}

export function useWizardInit({
  post,
  accounts,
  accountIdsForPost,
  design,
  template,
  isGalleryFlow,
  isSelectTemplateFlow,
  emphasizedPlatform,
  hasSeenOnboarding,
  hasSeenConfetti,
  hasUserPublishedBefore,
  isSocialMediaWizardAiCaptionsEnabled,
  isGoogleBusinessProfileEnabled,
  numParentAccountsConnected,
  stepOrder,
  setDesignsToPublish,
}: UseWizardInitOpts): void {
  const dispatch = useAppDispatch();
  const wizardStep = useWizardStep();
  const connectedAccounts = useWizardConnectedAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const captionErrors = useWizardCaptionErrors();
  const titleErrors = useWizardTitleErrors();
  const publishingAction = useSocialPostPublishingAction();

  useEffect((): void => {
    consumeWizardOriginCookie();

    const startStep = getStartWizardStep(post, accounts, {
      isGalleryFlow: isSelectTemplateFlow,
      template,
      emphasizedPlatform,
      stepOrder,
    });

    dispatch(startWizardAt(startStep));

    dispatch(updateDesignSelectorTab(getInitialDesignSelectorTab(getDeepLinkedDesignSelectorTab(), isGalleryFlow)));
    dispatch(updateHasSeenOnboarding(hasSeenOnboarding));
    dispatch(updateHasSeenConfetti(hasSeenConfetti));
    dispatch(updateHasUserPublishedBefore(hasUserPublishedBefore));
    dispatch(updateIsSocialMediaWizardAiCaptionsEnabled(isSocialMediaWizardAiCaptionsEnabled));
    dispatch(updateIsGoogleBusinessProfileEnabled(isGoogleBusinessProfileEnabled));
    dispatch(updateNumParentAccountsConnected(numParentAccountsConnected));

    const sanitizedPlatformToConnect = getSanitizedEmphasizedPlatform(emphasizedPlatform);

    if (sanitizedPlatformToConnect) {
      dispatch(updateEmphasizedPlatform(sanitizedPlatformToConnect));
      dispatch(updateAccountTypeToConnect(sanitizedPlatformToConnect));
    }

    const captionErrorAccount = getCaptionErrorAccountId(accountIdsToPublishTo, captionErrors);
    const titleErrorAccount = getTitleErrorAccountId(accountIdsToPublishTo, titleErrors);
    const isAnyAccountErrorProne = captionErrorAccount !== null || titleErrorAccount !== null;

    if (wizardStep === SocialMediaWizardStep.PUBLISH && isAnyAccountErrorProne) {
      dispatch(updatePublishingAction(undefined));
    } else if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      dispatch(initPublishingParams());
      dispatch(syncPublishingParams());
      void dispatch(
        savePreferredProfileSelectionUser({
          socialMediaAccountIds: accountIdsToPublishTo,
          idPost: post?.id,
        })
      );
    }

    if (wizardStep === SocialMediaWizardStep.PUBLISH) {
      if (publishingAction === undefined && !isAnyAccountErrorProne) {
        dispatch(updatePublishingAction(SocialPostPublishingAction.PUBLISH));
      }
    } else {
      dispatch(updatePublishingAction(undefined));
    }

    if (connectedAccounts.length === 0) {
      dispatch(disableNextButton());
    } else if (accountIdsToPublishTo.length !== 0) {
      dispatch(enableNextButton());
    }

    if (accountIdsForPost.length > 0) {
      dispatch(updateAccountIdsToPublishTo(accountIdsForPost));
    }

    if (design) {
      dispatch(updateDesignsToPublish([design]));
    }

    if (isGalleryFlow) {
      dispatch(updateIsTemplateSelected(true));
    }

    if (template) {
      dispatch(updateIsTemplateSelected(true));
      dispatch(updateDesignsToPublish([template]));
      setDesignsToPublish([template]);
    }

    if (post) {
      const {publishingParams} = post;
      if (publishingParams) {
        dispatch(updatePublishingParams(publishingParams));
      }

      dispatch(updatePost(post));
    }
  }, []);
}

export function useTrackWizardStepViewed({wizardStep, hasStartedLoggedOut, hasResolvedStartStep}: UseTrackWizardStepViewedOpts): void {
  useEffect((): void => {
    if (!hasResolvedStartStep) {
      return;
    }
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_STEPVIEWED, {
      [GA4EventParamName.SECTION]: getStepViewedSection(wizardStep),
      [GA4EventParamName.TYPE]: getStepViewedType(hasStartedLoggedOut),
    });
  }, [wizardStep, hasResolvedStartStep, hasStartedLoggedOut]);
}

export function useHasReachedReviewStep(wizardStep: SocialMediaWizardStep): boolean {
  const [hasReachedReviewStep, setHasReachedReviewStep] = useState(false);

  useEffect((): void => {
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS || wizardStep === SocialMediaWizardStep.PUBLISH) {
      setHasReachedReviewStep(true);
    }
  }, [wizardStep]);

  return hasReachedReviewStep;
}

export function useWizardScrollState(scrollContainerRef: RefObject<HTMLDivElement | null>, wizardStep: SocialMediaWizardStep): UseWizardScrollStateResult {
  const [isContentHeadingHidden, setIsContentHeadingHidden] = useState(false);
  const [isContentScrolled, setIsContentScrolled] = useState(false);

  const updateScrollDerivedState = useCallback((): void => {
    const containerElement = scrollContainerRef.current;
    if (!containerElement) {
      return;
    }

    const {scrollTop, clientHeight, scrollHeight} = containerElement;
    const isContentScrollable = scrollHeight > clientHeight;

    setIsContentScrolled(isContentScrollable && scrollTop > WIZARD_CONTENT_SCROLL_EDGE_THRESHOLD_PX);

    const headingElement = containerElement.querySelector(WIZARD_HEADER_SELECTOR);
    if (!headingElement) {
      setIsContentHeadingHidden(false);
      return;
    }

    setIsContentHeadingHidden(headingElement.getBoundingClientRect().bottom <= containerElement.getBoundingClientRect().top);
  }, [scrollContainerRef]);

  useEffect(() => {
    const containerElement = scrollContainerRef.current;
    if (!containerElement) {
      return;
    }

    updateScrollDerivedState();
    containerElement.addEventListener('scroll', updateScrollDerivedState, {passive: true});

    const contentResizeObserver = new ResizeObserver(updateScrollDerivedState);
    contentResizeObserver.observe(containerElement);
    Array.from(containerElement.children).forEach((childElement) => {
      contentResizeObserver.observe(childElement);
    });

    return (): void => {
      containerElement.removeEventListener('scroll', updateScrollDerivedState);
      contentResizeObserver.disconnect();
    };
  }, [scrollContainerRef, updateScrollDerivedState, wizardStep]);

  return {isContentHeadingHidden, isContentScrolled};
}

/** Shared by the Review step's account header and the account rail, so both honour the same opt-out prompt. */
export function useRemoveAccountFromPost(account: SocialAccount): VoidFunction {
  const dispatch = useAppDispatch();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  const doRemoveAccountFromPost = (): void => {
    if (accountIdsToPublishTo.length === 1) {
      dispatch(updatePublishingParams({}));
      dispatch(updateWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS));
      return;
    }

    dispatch(removeAccountIdFromPublishing(account.id));
    dispatch(removeAccountFromPublishingParams(account.id));
  };

  const openRemoveAccountConfirmationPrompt = (): void => {
    openConfirmDeleteModal({
      title: window.i18next.t('pmwjs_remove_account'),
      text: window.i18next.t('pmwjs_removing_account_description'),
      ctaText: window.i18next.t('pmwjs_remove'),
      onDeleteConfirmation: doRemoveAccountFromPost,
      showPermanentClosureCheckbox: true,
      onPermanentClosureCheckboxConfirmation: (): void => {
        void doHideRemoveAccountConfirmationForUser();
      },
    });
  };

  return (): void => {
    void (async (): Promise<void> => {
      if (await getHideSMRemoveAccountConfirmationPreference()) {
        doRemoveAccountFromPost();
        return;
      }

      openRemoveAccountConfirmationPrompt();
    })();
  };
}
